import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { plansService } from '../services/plans-service';
import { UsageSummary } from '../types/user';

interface UsageIndicatorProps {
  tenantId: string;
  userId: string;
  className?: string;
  onUpgradeClick?: () => void;
}

export interface UsageIndicatorRef {
  refreshUsage: () => Promise<void>;
}

const UsageIndicator = forwardRef<UsageIndicatorRef, UsageIndicatorProps>(
  ({ tenantId, userId, className = '', onUpgradeClick }, ref) => {
    const [usage, setUsage] = useState<UsageSummary | null>(null);
    const [loading, setLoading] = useState(true);

    const loadUsage = async () => {
      try {
        const usageData = await plansService.getDailyUsage(tenantId, userId);
        setUsage(usageData);
      } catch (error) {
        console.error('❌ Erro ao carregar uso:', error);
      } finally {
        setLoading(false);
      }
    };

    // Expor função para componente pai
    useImperativeHandle(ref, () => ({
      refreshUsage: loadUsage
    }));

    useEffect(() => {
      loadUsage();
      // Recarregar a cada 30 segundos (backup)
      const interval = setInterval(loadUsage, 30000);
      return () => clearInterval(interval);
    }, [tenantId, userId]);

    if (loading) {
      return (
        <div className={`flex items-center space-x-2 ${className}`}>
          <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
          <div className="w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
        </div>
      );
    }

    if (!usage) {
      return null;
    }

    const getStatusColor = () => {
      if (usage.isUnlimited) return 'text-green-600';
      if (usage.percentageUsed >= 90) return 'text-red-600';
      if (usage.percentageUsed >= 70) return 'text-yellow-600';
      return 'text-green-600';
    };

    const getProgressColor = () => {
      if (usage.isUnlimited) return 'bg-green-500';
      if (usage.percentageUsed >= 90) return 'bg-red-500';
      if (usage.percentageUsed >= 70) return 'bg-yellow-500';
      return 'bg-green-500';
    };

    const formatUsageText = () => {
      if (usage.isUnlimited) {
        return `${usage.totalMessages} mensagens (Ilimitado)`;
      }
      return `${usage.totalMessages}/${usage.limitMessages} mensagens`;
    };

    // Se for usuário Premium (unlimited), não mostrar nada
    if (usage.isUnlimited) {
      return null;
    }

    // Se for FREE, mostrar contador + botão upgrade
    return (
      <div className={`flex items-center space-x-3 ${className}`}>
        {/* Contador de uso */}
        <div className="flex items-center space-x-2">
          <span className={`text-xs font-medium ${getStatusColor()}`}>
            {formatUsageText()}
          </span>
          
          {/* Barra de progresso */}
          <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className={`h-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${Math.min(100, usage.percentageUsed)}%` }}
            />
          </div>
        </div>

        {/* Botão de upgrade (sempre visível para usuários FREE) */}
        <button
          onClick={onUpgradeClick}
          className="text-xs bg-white/10 text-white px-3 py-1.5 rounded-lg hover:bg-white/20 transition-all duration-200 font-medium border border-white/20"
        >
          ⚡ Upgrade Premium
        </button>
      </div>
    );
  }
);

UsageIndicator.displayName = 'UsageIndicator';

export default UsageIndicator;
